import { Flex, Group } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAgentSessionFacets } from '../../api/agent-miner-client/agent-miner-client.hooks';
import { FilterPanel } from '../../components/filters/filter-panel';
import { PageLayout } from '../../components/page-layout/page-layout';
import { useFeatureFlag } from '../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../helpers/feature-flags/feature-flags.type';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useAgentMinerStore } from '../../store/agent-miner-store/agent-miner-store';
import { useGlobalStore } from '../../store/global-store/global-store';
import { baseWhite, inkLight, secondaryBase } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Icon } from '../../ui-library/icon/icon';
import { H2, Text } from '../../ui-library/typography/typography';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { useFlora } from '../flora/flora.context';
import {
  ADOPTION_TAB_LOCAL_PARAMS,
  MODEL_COMPARISON_TAB_LOCAL_PARAMS,
  MODEL_COMPARISON_URL_PARAMS,
} from './agent-miner.constants';
import { AI_ADOPTION_TABS, AgentMinerTab, resolveTabLabel } from './agent-miner.type';
import { getAgentMinerFloraContext } from './agent-miner-flora-context';
import { useModelComparisonParams } from './model-comparison/model-comparison.hooks';
import { AdoptionFilters, AdoptionMain, AdoptionOverview } from './adoption/adoption';
import {
  DeliveryImpactFilters,
  DeliveryImpactMain,
  DeliveryImpactSessionsPanel,
} from './delivery-impact/delivery-impact';
import {
  ModelComparisonFilters,
  ModelComparisonMain,
  ModelComparisonOverview,
  ModelComparisonSessionsPanel,
} from './model-comparison/model-comparison';
import { SessionPanel } from './session-panel/session-panel';
import { TaskFitTasksPanel } from './task-fit/task-fit-tasks-panel';
import { TaskFitFilters, TaskFitMain, TaskFitOverview } from './task-fit/task-fit';
import { FiltersHeaderSection } from './sessions-list/filters/filters-header-section';
import { getSessionsFilterGroups } from './sessions-list/filters/sessions-filter-groups.helpers';
import { useSessionsFilters } from './sessions-list/filters/sessions-filters.hooks';
import { SessionsList } from './sessions-list/sessions-list';

export function AgentMiner() {
  useDocumentTitle('Agent Miner - Bloomfilter');

  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSessionId = searchParams.get('session');
  // Model Comparison's drill-down: the family whose sessions the side panel
  // lists. Read here (not just inside the tab) because PageLayout owns the
  // side section for every tab.
  const selectedSessionsFamily = searchParams.get('sessions_family');
  // Delivery Impact's drill-down: whether the AI-sessions side panel is open.
  const isDeliveryImpactPanelOpen = searchParams.get('di_panel') === 'ai';
  // Task Fit's drill-down: a cell address opens the tasks-behind-the-cell
  // panel. Both halves must be present — a lone stale half reads as closed.
  const isTaskFitCellPanelOpen = !!searchParams.get('taskfit_cell_row') && !!searchParams.get('taskfit_cell_family');

  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const teams = useGlobalStore((state) => state.teams);
  const { data: facets } = useAgentSessionFacets(organizationId);
  const { setContextualInfo } = useFlora();

  const filters = useSessionsFilters();
  const [showFilters, setShowFilters] = useState(true);

  const isAiInsights = useFeatureFlag(FeatureFlag.AiInsights);

  // Active tab lives in the `tab` search param; when the param is absent
  // (e.g. arriving via the sidebar), fall back to the last-visited tab from
  // the store. Unknown values — or AI-adoption tabs while the flag is off —
  // fall back to Sessions so a stale deep link or stale stored tab can't
  // strand the user on a hidden view.
  const storedTab = useAgentMinerStore((state) => state.activeTab);
  const setStoredTab = useAgentMinerStore((state) => state.setActiveTab);
  const isValidTab = (tab: string | null): tab is AgentMinerTab =>
    tab === AgentMinerTab.Sessions || (isAiInsights && AI_ADOPTION_TABS.some((tabDef) => tabDef.id === tab));
  const rawTab = searchParams.get('tab');
  // Only fall back to the stored tab when the URL omits `tab`; a present-but-
  // unknown value resolves to Sessions.
  const activeTab: AgentMinerTab = isValidTab(rawTab)
    ? rawTab
    : rawTab === null && isValidTab(storedTab)
      ? storedTab
      : AgentMinerTab.Sessions;
  const isSessionsTab = activeTab === AgentMinerTab.Sessions;
  const isModelComparisonTab = activeTab === AgentMinerTab.ModelComparison;
  const isDeliveryImpactTab = activeTab === AgentMinerTab.DeliveryImpact;
  const isTaskFitTab = activeTab === AgentMinerTab.TaskFit;
  const isAdoptionTab = activeTab === AgentMinerTab.Adoption;
  // Every AI Insights tab resolves the same date window and allocation type.
  // Keep this hook at the page boundary so Flora receives the effective values
  // that the tab's query uses, rather than raw (and possibly stale) URL input.
  const aiInsightsParams = useModelComparisonParams();
  // The tab the URL explicitly selected (a valid `tab` param), or null when the
  // param is absent/unknown/gated. Only this ever gets persisted to the store.
  const explicitTab: AgentMinerTab | null = isValidTab(rawTab) ? rawTab : null;

  // The generic Flora payload is sent with every question and the backend adds
  // it to the agent's system prompt as default scope. It deliberately carries
  // no loaded chart/table data: the Agent Miner tools fetch fresh results.
  const searchParamString = searchParams.toString();
  const floraContext = useMemo(
    () =>
      getAgentMinerFloraContext({
        activeTab,
        portfolio,
        teams: teams ?? [],
        startDate: aiInsightsParams.start_date,
        endDate: aiInsightsParams.end_date,
        allocation: aiInsightsParams.time_allocation_type,
        searchParams: new URLSearchParams(searchParamString),
      }),
    [
      activeTab,
      portfolio,
      teams,
      aiInsightsParams.start_date,
      aiInsightsParams.end_date,
      aiInsightsParams.time_allocation_type,
      searchParamString,
    ],
  );

  useEffect(() => {
    setContextualInfo(floraContext);
  }, [floraContext, setContextualInfo]);

  // Remember the resolved tab, and canonicalize the URL when the store (not
  // the URL) chose it, so the address stays shareable.
  //
  // Only persist a tab the URL EXPLICITLY selected — never a store- or
  // fallback-derived value. When AI-Insights is gated off (e.g. a stale org
  // before the flag refetches), a remembered AI-adoption tab degrades to
  // Sessions here; writing that back would permanently overwrite the stored
  // preference with no recovery. `handleSelectTab` is the only other writer and
  // it only ever persists explicit user selections.
  useEffect(() => {
    if (explicitTab) setStoredTab(explicitTab);
    if (!rawTab && activeTab !== AgentMinerTab.Sessions) {
      const copy = new URLSearchParams(searchParams);
      copy.set('tab', activeTab);
      setSearchParams(copy, { replace: true });
    }
  }, [activeTab, explicitTab, rawTab, searchParams, setSearchParams, setStoredTab]);

  const writeSession = (nextId: string | null) => {
    const copy = new URLSearchParams(searchParams);
    if (nextId) copy.set('session', nextId);
    else copy.delete('session');
    setSearchParams(copy, { replace: true });
  };

  // Switch tabs via the URL, and drop any open session selection so the side
  // panel never lingers over a non-Sessions view. The store MUST be written
  // here too: Sessions is represented by an ABSENT `tab` param, and the
  // resolution above falls back to the stored tab when the param is absent —
  // without this write, clicking Sessions would bounce straight back to the
  // previously stored tab.
  const handleSelectTab = (nextTab: AgentMinerTab) => {
    setStoredTab(nextTab);
    const copy = new URLSearchParams(searchParams);
    // The selected session always closes on a tab switch.
    copy.delete('session');
    // `di_view`, `di_panel`, and `di_chart_view` only drive Delivery Impact, so
    // no other tab reads them — drop them on every switch, including between
    // AI-adoption tabs, the same way the Model-Comparison-local params are
    // dropped below.
    copy.delete('di_view');
    copy.delete('di_panel');
    copy.delete('di_chart_view');
    // Task Fit's drill-down address is tab-local; no other tab reads it, so
    // drop it on every switch to avoid re-opening a stale panel on return.
    copy.delete('taskfit_cell_row');
    copy.delete('taskfit_cell_family');
    // Same treatment for the Adoption-only params: no other tab reads them.
    // Guarded so re-clicking the already-active Adoption tab doesn't wipe the
    // user's view/measure/team selection — only drop them when leaving Adoption.
    if (nextTab !== AgentMinerTab.Adoption) {
      for (const key of ADOPTION_TAB_LOCAL_PARAMS) copy.delete(key);
    }
    if (nextTab === AgentMinerTab.Sessions) {
      copy.delete('tab');
      // Sessions ignores every MC/Usage param — strip the full set so none
      // lingers and silently reactivates on return (uses the same shared set as
      // the drill-down "expand to Sessions" path in model-comparison.tsx).
      for (const key of MODEL_COMPARISON_URL_PARAMS) copy.delete(key);
    } else {
      copy.set('tab', nextTab);
      // Between AI-adoption tabs, keep the cross-tab-shared filters
      // (view/allocation/model_provider/hidden_families) and drop only the
      // view-local params and any open drill-down, so returning to Model
      // Comparison restores the user's configuration.
      for (const key of MODEL_COMPARISON_TAB_LOCAL_PARAMS) copy.delete(key);
    }
    setSearchParams(copy, { replace: true });
  };

  // Mutual-exclusion: the filter panel and the session side panel never show
  // at the same time. Opening one closes the other so the main table area
  // isn't squeezed into horizontal overflow.
  const handleSelectSession = (nextId: string | null) => {
    if (nextId) setShowFilters(false);
    writeSession(nextId);
  };

  const handleSetShowFilters = (next: boolean) => {
    if (next && (selectedSessionId || selectedSessionsFamily || isDeliveryImpactPanelOpen || isTaskFitCellPanelOpen)) {
      const copy = new URLSearchParams(searchParams);
      copy.delete('session');
      copy.delete('sessions_family');
      copy.delete('di_panel');
      copy.delete('taskfit_cell_row');
      copy.delete('taskfit_cell_family');
      setSearchParams(copy, { replace: true });
    }
    setShowFilters(next);
  };

  // The Model Comparison, Delivery Impact, and Task Fit panels are opened from
  // the main section rather than through handleSelectSession, so mirror that
  // handler's mutual exclusion here: the filter panel folds away as soon as a
  // drill-down opens.
  useEffect(() => {
    // isTaskFitCellPanelOpen is tab-agnostic (it only reads the URL params), so
    // gate it on the Task Fit tab being active — otherwise a leftover cell param
    // would suppress the filter panel on other tabs.
    if (selectedSessionsFamily || isDeliveryImpactPanelOpen || (isTaskFitTab && isTaskFitCellPanelOpen))
      setShowFilters(false);
  }, [selectedSessionsFamily, isDeliveryImpactPanelOpen, isTaskFitTab, isTaskFitCellPanelOpen]);

  const filterGroups = getSessionsFilterGroups({
    facets,
    value: filters.value,
    setSources: filters.setSources,
    setUserIds: filters.setUserIds,
    setLabels: filters.setLabels,
    setHasTask: filters.setHasTask,
    setHideEmpty: filters.setHideEmpty,
    setModelFamilies: filters.setModelFamilies,
    t,
  });

  const filterPanel = (
    <FilterPanel
      filterGroups={filterGroups}
      headerSection={
        <FiltersHeaderSection
          value={filters.value}
          setQ={filters.setQ}
          setDateRange={filters.setDateRange}
          setCustomRange={filters.setCustomRange}
        />
      }
      onResetFilters={filters.reset}
      onClose={() => handleSetShowFilters(false)}
    />
  );

  const openExternalTab = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const header = (
    <Group>
      <Icon name="terminal" color={secondaryBase} size={36} />
      <H2>Agent Miner</H2>
    </Group>
  );

  const tabs = (
    <Group gap="xs">
      {isAiInsights &&
        AI_ADOPTION_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <Button
              key={tab.id}
              size="xs"
              radius="xl"
              variant={isActive ? 'secondary' : 'outline'}
              onClick={() => handleSelectTab(tab.id)}
              leftSection={
                <Icon
                  name={tab.icon}
                  color={isActive ? baseWhite : secondaryBase}
                  size={16}
                  style={{ cursor: 'pointer' }}
                />
              }
            >
              {resolveTabLabel(tab, t)}
            </Button>
          );
        })}
      <Button
        size="xs"
        radius="xl"
        variant={isSessionsTab ? 'secondary' : 'outline'}
        onClick={() => handleSelectTab(AgentMinerTab.Sessions)}
        leftSection={
          <Icon
            name="folder"
            color={isSessionsTab ? baseWhite : secondaryBase}
            size={16}
            style={{ cursor: 'pointer' }}
          />
        }
      >
        Sessions
      </Button>
      <Button
        size="xs"
        radius="xl"
        variant="outline"
        onClick={() => openExternalTab('/application/financials?tab=agent')}
        leftSection={<Icon name="payments" color={secondaryBase} size={16} style={{ cursor: 'pointer' }} />}
        rightSection={<Icon name="open_in_new" color={secondaryBase} size={14} style={{ cursor: 'pointer' }} />}
      >
        Financials
      </Button>
      <Button
        size="xs"
        radius="xl"
        variant="outline"
        onClick={() => openExternalTab('/application/process-analysis?activeTab=agents')}
        leftSection={<Icon name="auto_graph" color={secondaryBase} size={16} style={{ cursor: 'pointer' }} />}
        rightSection={<Icon name="open_in_new" color={secondaryBase} size={14} style={{ cursor: 'pointer' }} />}
      >
        Dashboard
      </Button>
    </Group>
  );

  const activeTabDef = AI_ADOPTION_TABS.find((tab) => tab.id === activeTab);

  // Scaffolding: the remaining AI-adoption tabs share the Sessions PageLayout
  // and render a placeholder until their charts and tables are built out.
  const mainSection = isSessionsTab ? (
    <SessionsList
      filterValue={filters.value}
      onSelectSession={handleSelectSession}
      selectedSessionId={selectedSessionId}
    />
  ) : isModelComparisonTab ? (
    <ModelComparisonMain />
  ) : isDeliveryImpactTab ? (
    <DeliveryImpactMain />
  ) : isTaskFitTab ? (
    <TaskFitMain />
  ) : isAdoptionTab ? (
    <AdoptionMain />
  ) : (
    <Flex direction="column" align="center" justify="center" gap={12} h="100%">
      <Icon name={activeTabDef?.icon ?? 'insights'} color={inkLight} size={48} />
      <Text size="regular" color={inkLight}>
        {activeTabDef ? `${resolveTabLabel(activeTabDef, t)} is coming soon.` : 'Coming soon.'}
      </Text>
    </Flex>
  );

  const modelComparisonFilterPanel = (
    <ModelComparisonFilters
      value={filters.value}
      setDateRange={filters.setDateRange}
      setCustomRange={filters.setCustomRange}
      onClose={() => handleSetShowFilters(false)}
    />
  );

  const deliveryImpactFilterPanel = (
    <DeliveryImpactFilters
      value={filters.value}
      setDateRange={filters.setDateRange}
      setCustomRange={filters.setCustomRange}
      onClose={() => handleSetShowFilters(false)}
    />
  );

  const taskFitFilterPanel = (
    <TaskFitFilters
      value={filters.value}
      setDateRange={filters.setDateRange}
      setCustomRange={filters.setCustomRange}
      onClose={() => handleSetShowFilters(false)}
    />
  );

  const adoptionFilterPanel = (
    <AdoptionFilters
      value={filters.value}
      setDateRange={filters.setDateRange}
      setCustomRange={filters.setCustomRange}
      onClose={() => handleSetShowFilters(false)}
    />
  );

  const sideFilters = isSessionsTab
    ? { content: filterPanel, show: showFilters, setShow: handleSetShowFilters }
    : isModelComparisonTab
      ? { content: modelComparisonFilterPanel, show: showFilters, setShow: handleSetShowFilters }
      : isDeliveryImpactTab
        ? { content: deliveryImpactFilterPanel, show: showFilters, setShow: handleSetShowFilters }
        : isTaskFitTab
          ? { content: taskFitFilterPanel, show: showFilters, setShow: handleSetShowFilters }
          : isAdoptionTab
            ? { content: adoptionFilterPanel, show: showFilters, setShow: handleSetShowFilters }
            : undefined;

  return (
    <MenuDrivenContainer header={header}>
      <PageLayout
        controlBar={null}
        tabs={tabs}
        overview={
          // Adoption brings its own KPI strip; Delivery Impact reuses the
          // Model Comparison strip (the same org-wide sessions/cycle/
          // throughput/rework overview applies to both, and the shared query
          // is cached across them). Task Fit has a dedicated overview.
          isAdoptionTab
            ? { title: t('agent_miner.adoption.overview_title'), content: <AdoptionOverview /> }
            : isModelComparisonTab || isDeliveryImpactTab
              ? { title: t('agent_miner.model_comparison.overview_title'), content: <ModelComparisonOverview /> }
              : isTaskFitTab
                ? { title: t('agent_miner.task_fit.overview_title'), content: <TaskFitOverview /> }
                : undefined
        }
        sideFilters={sideFilters}
        mainSection={mainSection}
        sideSection={
          isModelComparisonTab ? (
            <ModelComparisonSessionsPanel />
          ) : isDeliveryImpactTab ? (
            <DeliveryImpactSessionsPanel />
          ) : isTaskFitTab ? (
            <TaskFitTasksPanel />
          ) : (
            <SessionPanel sessionId={selectedSessionId} onClose={() => handleSelectSession(null)} />
          )
        }
        showSideSection={
          isModelComparisonTab
            ? !!selectedSessionsFamily
            : isDeliveryImpactTab
              ? isDeliveryImpactPanelOpen
              : isTaskFitTab
                ? isTaskFitCellPanelOpen
                : isSessionsTab && !!selectedSessionId
        }
      />
    </MenuDrivenContainer>
  );
}
